import React, { Component, ReactNode } from 'react';
import { errorLogger } from '../../services/errorLogger';

interface NetworkErrorBoundaryProps {
  children: ReactNode;
  onRetry?: () => void;
  maxRetries?: number;
}

interface NetworkErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  retryCount: number;
  isRetrying: boolean;
  nextRetryDelay: number;
}

export class NetworkErrorBoundary extends Component<NetworkErrorBoundaryProps, NetworkErrorBoundaryState> {
  private readonly COMPONENT_NAME = 'NetworkErrorBoundary';
  private readonly BASE_DELAY = 1000; // 1 second
  private readonly MAX_DELAY = 8000; // 8 seconds
  private retryTimeout?: NodeJS.Timeout;

  constructor(props: NetworkErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      retryCount: 0,
      isRetrying: false,
      nextRetryDelay: this.BASE_DELAY
    };
  }

  componentWillUnmount(): void {
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
    }
  }

  static getDerivedStateFromError(error: Error): Partial<NetworkErrorBoundaryState> {
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    errorLogger.logError(error, this.COMPONENT_NAME, {
      componentStack: errorInfo.componentStack,
      retryCount: this.state.retryCount,
      isNetworkError: this.isNetworkRelatedError(error)
    });
  }

  private isNetworkRelatedError(error: Error): boolean {
    const message = error.message.toLowerCase();
    return (
      message.includes('fetch') ||
      message.includes('network') ||
      message.includes('timeout') ||
      message.includes('cors') ||
      message.includes('connection') ||
      message.includes('502') ||
      message.includes('503') ||
      message.includes('504') ||
      error.name === 'NetworkError' ||
      (error.name === 'TypeError' && message.includes('failed to fetch'))
    );
  }

  private getErrorType(error: Error): 'network' | 'timeout' | 'rate-limit' | 'server' | 'offline' | 'unknown' {
    const message = error.message.toLowerCase();
    
    if (!navigator.onLine) return 'offline';
    if ((message.includes('429') || message.includes('rate limit'))) return 'rate-limit';
    if (message.includes('timeout')) return 'timeout';
    if ((message.includes('502') || message.includes('503')) || message.includes('504')) return 'server';
    if (this.isNetworkRelatedError(error)) return 'network';
    
    return 'unknown';
  }

  private calculateRetryDelay(retryCount: number): number {
    // Exponential backoff with jitter: 1s, 2s, 4s, 8s
    const baseDelay = Math.min(this.BASE_DELAY * Math.pow(2, retryCount), this.MAX_DELAY);
    // Add jitter (±25% of base delay) to prevent thundering herd
    const jitter = baseDelay * 0.25 * (Math.random() - 0.5);
    return Math.max(baseDelay + jitter, 500); // Minimum 500ms
  }

  private handleRetry = (): void => {
    const maxRetries = this.props.maxRetries ?? 4;
    
    if (this.state.retryCount >= maxRetries) {
      return;
    }

    const delay = this.calculateRetryDelay(this.state.retryCount);
    
    this.setState({
      isRetrying: true,
      nextRetryDelay: delay
    });

    this.retryTimeout = setTimeout(() => {
      this.setState(prevState => ({
        hasError: false,
        error: undefined,
        retryCount: prevState.retryCount + 1,
        isRetrying: false,
        nextRetryDelay: this.BASE_DELAY
      }));

      // Call custom retry handler if provided
      if (this.props.onRetry) {
        this.props.onRetry();
      }
    }, delay);
  };

  private handleReset = (): void => {
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
    }
    
    this.setState({
      hasError: false,
      error: undefined,
      retryCount: 0,
      isRetrying: false,
      nextRetryDelay: this.BASE_DELAY
    });
  };

  private renderErrorMessage(): ReactNode {
    if (!this.state.error) return null;

    const errorType = this.getErrorType(this.state.error);
    const maxRetries = this.props.maxRetries ?? 4;

    const messages = {
      offline: {
        title: '🌐 You appear to be offline',
        message: 'Check your internet connection and try again.'
      },
      'rate-limit': {
        title: '⏰ Too many requests',
        message: 'Please wait a moment before trying again. We\'re preventing overload.'
      },
      timeout: {
        title: '⏱️ Request timed out',
        message: 'The server took too long to respond. This usually resolves quickly.'
      },
      server: {
        title: '🔧 Server temporarily unavailable',
        message: 'Our servers are experiencing issues. We\'re working to fix this.'
      },
      network: {
        title: '📡 Network error',
        message: 'There was a problem connecting to our servers.'
      },
      unknown: {
        title: '❌ Something went wrong',
        message: 'An unexpected error occurred. Please try again.'
      }
    };

    const { title, message } = messages[errorType];

    return (
      <div className="border border-border rounded-lg p-4 bg-card">
        <div className="text-destructive text-sm font-medium mb-2">
          {title}
        </div>
        <div className="text-muted-foreground text-sm mb-4">
          {message}
        </div>
        
        <div className="flex items-center gap-2 mb-3">
          {this.state.retryCount < maxRetries && (
            <>
              {this.state.isRetrying ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                  Retrying in {Math.ceil(this.state.nextRetryDelay / 1000)}s...
                </div>
              ) : (
                <button
                  onClick={this.handleRetry}
                  className="px-3 py-1 text-sm bg-primary text-primary-foreground rounded hover:bg-primary/90"
                >
                  Retry ({maxRetries - this.state.retryCount} left)
                </button>
              )}
            </>
          )}
          
          <button
            onClick={this.handleReset}
            className="px-3 py-1 text-sm bg-muted text-muted-foreground rounded hover:bg-muted/80"
          >
            Reset
          </button>
        </div>

        {errorType === 'rate-limit' && (
          <div className="text-xs text-muted-foreground border-l-4 border-primary pl-3">
            💡 Rate limiting helps keep the service stable for everyone. Thanks for your patience!
          </div>
        )}

        {errorType === 'offline' && (
          <div className="text-xs text-muted-foreground border-l-4 border-primary pl-3">
            💡 Some features may work offline using cached data.
          </div>
        )}
      </div>
    );
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return this.renderErrorMessage();
    }

    return this.props.children;
  }
}